import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClientService } from '../../../core/services/http-client.service';
import { CommonService } from '../../../core/services/common.service';

/**
 * Post-purchase Patakha Fodo page. Hosts the same design-tool bundle as the
 * /festive-social-media-offer-2 page but mounted via Store.dc.html with
 * {@code mode="store"} so the design renders the in-store flow:
 *
 *   login (email + mobile) → verify OTP →
 *     ├─ only store scratch_offer exists  → reveal that gift, show Gift पक्का card
 *     ├─ only online coupon exists        → show "आप online से आए हैं" nudge
 *     └─ both                              → choose-between-two-gifts UI → finalize
 *
 * The design's own React runtime drives animations, state transitions and the
 * choose/confirm card. Backend calls are proxied through a postMessage bridge:
 *
 *   otp:send      → store/otp/generateOTP?mobile=X          (shared with social-media)
 *   otp:verify    → store/signinWithOtp + /redemption-options-by-mobile
 *   gift:choose   → store/diwali/finalize                   (merge-aware)
 */
@Component({
  selector: 'app-festive-store-offer-2',
  templateUrl: './festive-store-offer-2.component.html',
  styleUrls: ['./festive-store-offer-2.component.css'],
})
export class FestiveStoreOffer2Component implements OnInit, OnDestroy {
  readonly src: SafeResourceUrl;
  private readonly onMessage = (ev: MessageEvent) => this.handleIframeMessage(ev);
  private authedMobile = '';

  constructor(
    sanitizer: DomSanitizer,
    private http: HttpClientService,
    private common: CommonService,
  ) {
    this.src = sanitizer.bypassSecurityTrustResourceUrl(
      '/assets/festive-store-offer-2/index.html'
    );
  }

  ngOnInit(): void {
    window.addEventListener('message', this.onMessage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
  }

  private handleIframeMessage(ev: MessageEvent): void {
    const msg = ev && ev.data;
    if (!msg || msg.__sd !== 1 || !msg.reqId || !msg.type) return;

    switch (msg.type) {
      case 'auth:probe':
        return this.handleAuthProbe(msg.reqId);
      case 'auth:logout':
        return this.handleAuthLogout(msg.reqId);
      case 'otp:send':
        return this.handleOtpSend(msg.reqId, msg.payload || {});
      case 'otp:verify':
        return this.handleOtpVerify(msg.reqId, msg.payload || {});
      case 'gift:choose':
        return this.handleGiftChoose(msg.reqId, msg.payload || {});
      default:
        this.reply(msg.reqId, false, null, 'unknown_type');
    }
  }

  /**
   * Called by the iframe on mount. If an authenticated mobile is already in localStorage
   * (either the main sdstore customer login OR a prior Diwali session) the iframe skips
   * the login modal and the user drops straight onto the appropriate stage. Response shape
   * mirrors otp:verify so the iframe can reuse __sdRouteFromRedemptionOptions.
   */
  private handleAuthProbe(reqId: string): void {
    const authedMobile = this.readAuthedMobile();
    if (!authedMobile) { this.reply(reqId, true, { authed: false }); return; }
    this.authedMobile = authedMobile;
    this.http.get('store/diwali/redemption-options-by-mobile', { mobile: authedMobile }).subscribe(
      (res: any) => {
        const payload = res?.response || res?.data || res || {};
        this.reply(reqId, true, {
          authed: true,
          mobile: authedMobile,
          online: payload.online ? this.shapeOnlineCoupon(payload.online) : null,
          smartphone: payload.smartphone ? this.shapeScratchOffer(payload.smartphone) : null,
          billed: !!payload.billed,
        });
      },
      () => this.reply(reqId, true, { authed: true, mobile: authedMobile, online: null, smartphone: null, billed: false }),
    );
  }

  /**
   * Iframe tells us the user tapped "Log out". Clear the full session (customer, token,
   * loggedIn flag, Diwali mobile, cart) so the next visit shows the login modal again.
   */
  private handleAuthLogout(reqId: string): void {
    try {
      this.common.setLoggedIn(false);
      this.common.removeAllStorage();   // localStorage.clear() — covers sd_diwali_authenticated_mobile too
    } catch { /* localStorage unavailable */ }
    this.authedMobile = '';
    this.reply(reqId, true, { cleared: true });
  }

  /** Resolve "which mobile is already authenticated in this browser" or '' if none. */
  private readAuthedMobile(): string {
    try {
      const loggedIn = this.common.isLoggedIn();
      const diwali = (localStorage.getItem('sd_diwali_authenticated_mobile') || '').trim();
      if (diwali && /^[6-9]\d{9}$/.test(diwali)) return diwali;
      if (loggedIn) {
        const customer = this.common.getStorage('customer') || this.common.getData();
        const m: string = (customer?.mobileNumber || customer?.mobile || '').trim();
        if (/^[6-9]\d{9}$/.test(m)) return m;
      }
    } catch { /* localStorage unavailable */ }
    return '';
  }

  /** SMS-delivered OTP via the standard login endpoint. Mirrors
   *  FestiveSocialMediaOffer2Component.handleOtpSend — email delivery has been retired now
   *  that the SMS DLT template is approved again. */
  private handleOtpSend(reqId: string, payload: { mobile?: string }): void {
    const mobile = String(payload.mobile || '').replace(/\s+/g, '');
    if (!/^[6-9]\d{9}$/.test(mobile)) return this.reply(reqId, false, null, 'bad_mobile');
    this.http.get('store/otp/generateOTP', { mobile }).subscribe(
      (data: any) => {
        const refId = data?.response?.reference_id || data?.reference_id || '';
        this.reply(reqId, true, { referenceId: refId });
      },
      () => this.reply(reqId, false, null, 'net'),
    );
  }

  private handleOtpVerify(reqId: string, payload: { mobile?: string; otp?: string; referenceId?: string }): void {
    const mobile = String(payload.mobile || '').replace(/\s+/g, '');
    const otp = String(payload.otp || '').trim();
    const referenceId = String(payload.referenceId || '');
    if (!/^[6-9]\d{9}$/.test(mobile) || !/^\d{4,6}$/.test(otp)) {
      this.reply(reqId, false, null, 'bad_input');
      return;
    }
    this.http.post('store/signinWithOtp', { mobile, otp, referenceId }).subscribe(
      () => this.hydrateAndFetchOptions(reqId, mobile),
      (err: any) => {
        const msg = err?.error?.message || err?.message || 'wrong_otp';
        this.reply(reqId, false, null, msg);
      },
    );
  }

  /**
   * Store flow needs BOTH the smartphone-flow scratch_offer AND any online coupon.
   * `/store/diwali/redemption-options-by-mobile` returns both in one call so the
   * iframe can decide between (reveal-single / show-online-nudge / choose-pair).
   */
  private hydrateAndFetchOptions(reqId: string, mobile: string): void {
    // "Last login wins" — if a different mobile is already authenticated in this browser
    // (either the main sdstore customer session OR a prior Diwali session), nuke the whole
    // session before installing the new one. Prevents the previous user's token / cart /
    // customer record from leaking into the new mobile's flow.
    this.clearStaleSessionIfMobileChanged(mobile);
    this.authedMobile = mobile;
    this.http.get('store/checkmobile/' + mobile).subscribe(
      (data: any) => {
        const customer = data?.response?.exists && data.response.customer;
        if (customer && customer.id) {
          this.common.setStorage('customer', customer);
          this.common.setLoggedIn(true);
        }
        // Remember the Diwali-authenticated mobile for cross-page continuity (/spin,
        // /festive-login both read this via readDiwaliStoredMobile()).
        try { localStorage.setItem('sd_diwali_authenticated_mobile', mobile); } catch { /* best-effort */ }
        this.fetchRedemptionOptions(reqId, mobile);
      },
      () => {
        try { localStorage.setItem('sd_diwali_authenticated_mobile', mobile); } catch { /* best-effort */ }
        this.fetchRedemptionOptions(reqId, mobile);
      },
    );
  }

  /**
   * When the newly-OTP'd mobile doesn't match whatever is currently in storage, clear
   * everything so no stale customer, cart or auth token carries over. Broadcasts the
   * logout via common.setLoggedIn(false) so subscribers (header, cart badge, etc.) react.
   * No-op when the same mobile logs in again — avoids wiping their own Diwali state.
   */
  private clearStaleSessionIfMobileChanged(newMobile: string): void {
    try {
      const existing = this.common.getStorage('customer') || this.common.getData();
      const existingMobile: string | undefined = existing?.mobileNumber || existing?.mobile;
      const existingDiwali = localStorage.getItem('sd_diwali_authenticated_mobile');
      const differentCustomer = !!existingMobile && existingMobile !== newMobile;
      const differentDiwali = !!existingDiwali && existingDiwali !== newMobile;
      if (differentCustomer || differentDiwali) {
        this.common.setLoggedIn(false);     // broadcasts logout to anything subscribed
        this.common.removeAllStorage();     // localStorage.clear()
      }
    } catch { /* localStorage can be unavailable (private-mode webviews) */ }
  }

  private fetchRedemptionOptions(reqId: string, mobile: string): void {
    this.http.get('store/diwali/redemption-options-by-mobile', { mobile }).subscribe(
      (res: any) => {
        const payload = res?.response || res?.data || res || {};
        this.reply(reqId, true, {
          mobile,
          online: payload.online ? this.shapeOnlineCoupon(payload.online) : null,
          smartphone: payload.smartphone ? this.shapeScratchOffer(payload.smartphone) : null,
          billed: !!payload.billed,
        });
      },
      () => this.reply(reqId, true, { mobile, online: null, smartphone: null, billed: false }),
    );
  }

  /** Merge choice: write chosen gift into ScratchOffer, restore the loser's stock. */
  private handleGiftChoose(reqId: string, payload: { scratchOfferId?: number; giftId?: number; mobile?: string }): void {
    const scratchOfferId = Number(payload.scratchOfferId || 0);
    const giftId = Number(payload.giftId || 0);
    const mobile = String(payload.mobile || this.authedMobile || '').replace(/\s+/g, '');
    if (scratchOfferId <= 0 || giftId <= 0 || !/^[6-9]\d{9}$/.test(mobile)) {
      return this.reply(reqId, false, null, 'bad_input');
    }
    this.http.post('store/diwali/finalize', { scratchOfferId, giftId, mobile }).subscribe(
      (res: any) => {
        const data = res?.data || res?.response || res || {};
        this.reply(reqId, true, {
          scratched: !!data.scratched,
          giftId: data.giftId || giftId,
          // Prefer the server-returned couponCode (scratch_offer.coupon_code written at
          // billing). Falls back to whatever the iframe already knew (__sdStoreCouponCode).
          couponCode: data.couponCode || '',
        });
      },
      (err: any) => {
        const msg = err?.error?.message || err?.message || 'finalize_failed';
        this.reply(reqId, false, null, msg);
      },
    );
  }

  private shapeOnlineCoupon(c: any): any {
    const gift = c.gift ? {
      id: c.gift.id,
      name: c.gift.name,
      thumbnailUrl: c.gift.thumbnailUrl || null,
      thumbnailFullUrl: this.resolveDocUrl(c.gift.thumbnailUrl),
    } : null;
    // finalGift mirrors what the backend attached for REDEEMED/OVERRIDDEN — the gift the
    // customer actually walked away with. Null for GENERATED (nothing finalized yet).
    const finalGift = c.finalGift ? {
      scratchOfferId: c.finalGift.scratchOfferId,
      invoiceNumber: c.finalGift.invoiceNumber || null,
      giftId: c.finalGift.giftId,
      scratched: !!c.finalGift.scratched,
      gift: c.finalGift.gift ? {
        id: c.finalGift.gift.id,
        name: c.finalGift.gift.name,
        thumbnailUrl: c.finalGift.gift.thumbnailUrl || null,
        thumbnailFullUrl: this.resolveDocUrl(c.finalGift.gift.thumbnailUrl),
      } : null,
    } : null;
    return {
      id: c.id,
      couponCode: c.couponCode,
      patakhaType: c.patakhaType,
      status: c.status,
      redeemedScratchOfferId: c.redeemedScratchOfferId || null,
      gift,
      finalGift,
    };
  }

  private shapeScratchOffer(s: any): any {
    const gift = s.gift ? {
      id: s.gift.id,
      name: s.gift.name,
      thumbnailUrl: s.gift.thumbnailUrl || null,
      thumbnailFullUrl: this.resolveDocUrl(s.gift.thumbnailUrl),
    } : null;
    return {
      id: s.id,
      giftId: s.giftId,
      scratched: !!s.scratched,
      invoiceNumber: s.invoiceNumber || null,
      // couponCode is now server-persisted on dtr.scratch_offer.coupon_code. Written at
      // billing time (ScratchService.persistScratchV2) or lazy-backfilled on first read
      // (DiwaliCouponController.toScratchOfferPayload → ensureScratchOfferCouponCode).
      couponCode: s.couponCode || '',
      gift,
    };
  }

  /**
   * Gift thumbnails are hosted on the central document CDN, not the per-env API host —
   * use the production CDN in every environment so dev doesn't 404 against localhost:8080.
   * Already-absolute URLs are returned as-is in case the backend ever ships a full link.
   */
  private resolveDocUrl(raw: any): string | null {
    const url = typeof raw === 'string' ? raw.trim() : raw != null ? String(raw) : '';
    if (!url) return null;
    if (/^https?:/i.test(url)) return url;
    return 'https://apis.smartdukaan.com/document/' + url;
  }

  private reply(reqId: string, ok: boolean, data: any, error?: string): void {
    const iframe = document.querySelector('iframe.fsmo2-frame') as HTMLIFrameElement | null;
    const win = iframe && iframe.contentWindow;
    if (!win) return;
    try {
      win.postMessage({ __sd: 1, replyTo: reqId, ok, data, error }, '*');
    } catch { /* iframe gone */ }
  }
}
